import {register as registerPwaServiceWorker} from './serviceWorkerRegistration';
import {
  getWebappBrowserTestQueryState,
  shouldHonorWebappBrowserTestQueryContract,
} from './browserTestHarness';
import {reportServiceWorkerFailure} from 'utils/handleServiceWorkerError';
import {ensureSyncMessageServiceWorkerReady} from 'utils/syncMessageServiceWorker';

/** Named service worker registrations that browser-test specs can control explicitly. */
export type ControlledServiceWorkerId = 'pwa' | 'sync-message';

/** Snapshot of a controlled service worker's current registration state. */
interface ServiceWorkerSnapshot {
  id: ControlledServiceWorkerId;
  requested: boolean;
  available: boolean;
  registered: boolean;
  controlling: boolean;
  scope: string | null;
  activeScriptUrl: string | null;
}

/** Summary of the current controlled service worker state for the page. */
interface ServiceWorkerListResult {
  requestedWorkers: ControlledServiceWorkerId[];
  workers: ServiceWorkerSnapshot[];
}

/** Browser helpers exposed for Playwright to inspect and manage service workers. */
type E2EServiceWorkersApi = {
  list: () => Promise<ServiceWorkerListResult>;
  unregisterAll: () => Promise<void>;
  waitFor: (
    ids: ControlledServiceWorkerId[],
    timeoutMs?: number,
  ) => Promise<ServiceWorkerListResult>;
};

type LegacyControlledServiceWorkerMode =
  | 'all'
  | 'none'
  | ControlledServiceWorkerId;

const BROWSER_TEST_SERVICE_WORKER_NAMESPACE = '__cipBrowserTest';
const KNOWN_SERVICE_WORKERS: readonly ControlledServiceWorkerId[] = [
  'sync-message',
  'pwa',
];
const LEGACY_SERVICE_WORKER_MODE_QUERY_PARAM = 'sw';
const SERVICE_WORKER_WAIT_INTERVAL_MS = 100;
const DEFAULT_SERVICE_WORKER_WAIT_TIMEOUT_MS = 5_000;
const PWA_SERVICE_WORKER_SCRIPT = 'service-worker.js';
const SYNC_MESSAGE_SERVICE_WORKER_SCRIPT = 'sync-message-sw.js';

function getLegacyRequestedServiceWorkerMode(): LegacyControlledServiceWorkerMode {
  const searchParams = new URLSearchParams(window.location.search);
  const requestedMode = searchParams.get(
    LEGACY_SERVICE_WORKER_MODE_QUERY_PARAM,
  ) as LegacyControlledServiceWorkerMode | null;

  if (!requestedMode) {
    return 'sync-message';
  }
  if (requestedMode === 'all' || requestedMode === 'none') {
    return requestedMode;
  }
  if (KNOWN_SERVICE_WORKERS.includes(requestedMode)) {
    return requestedMode;
  }
  return 'sync-message';
}

function getLegacyRequestedServiceWorkerIds(): ControlledServiceWorkerId[] {
  const requestedMode = getLegacyRequestedServiceWorkerMode();
  if (requestedMode === 'all') {
    return [...KNOWN_SERVICE_WORKERS];
  }
  if (requestedMode === 'none') {
    return [];
  }
  return [requestedMode];
}

function getRequestedServiceWorkerIds(): ControlledServiceWorkerId[] {
  if (shouldHonorWebappBrowserTestQueryContract()) {
    return getWebappBrowserTestQueryState().resolvedPlan?.workers ?? [];
  }

  return getLegacyRequestedServiceWorkerIds();
}

function getServiceWorkerIdFromScriptUrl(
  scriptUrl: string | null,
): ControlledServiceWorkerId | null {
  if (!scriptUrl) {
    return null;
  }
  if (scriptUrl.includes(SYNC_MESSAGE_SERVICE_WORKER_SCRIPT)) {
    return 'sync-message';
  }
  if (scriptUrl.includes(PWA_SERVICE_WORKER_SCRIPT)) {
    return 'pwa';
  }
  return null;
}

function getActiveScriptUrl(
  registration: ServiceWorkerRegistration,
): string | null {
  return (
    registration.active?.scriptURL ??
    registration.waiting?.scriptURL ??
    registration.installing?.scriptURL ??
    null
  );
}

function isServiceWorkerAvailable(id: ControlledServiceWorkerId): boolean {
  if (typeof window === 'undefined') {
    return false;
  }

  if (!('serviceWorker' in navigator)) {
    return false;
  }

  if (id === 'sync-message') {
    return true;
  }

  return import.meta.env.PROD;
}

async function listControlledServiceWorkers(): Promise<ServiceWorkerListResult> {
  const requestedIds = new Set(getRequestedServiceWorkerIds());
  const registrations = 'serviceWorker' in navigator
    ? await navigator.serviceWorker.getRegistrations()
    : [];

  const registrationById = new Map<
    ControlledServiceWorkerId,
    ServiceWorkerRegistration
  >();
  for (const registration of registrations) {
    const id = getServiceWorkerIdFromScriptUrl(getActiveScriptUrl(registration));
    if (!id) {
      continue;
    }
    registrationById.set(id, registration);
  }

  const controllerScriptUrl = navigator.serviceWorker?.controller?.scriptURL ?? null;
  return {
    requestedWorkers: [...requestedIds],
    workers: KNOWN_SERVICE_WORKERS.map(id => {
      const registration = registrationById.get(id);
      const activeScriptUrl = registration
        ? getActiveScriptUrl(registration)
        : null;

      return {
        id,
        requested: requestedIds.has(id),
        available: isServiceWorkerAvailable(id),
        registered: Boolean(registration),
        controlling: Boolean(
          activeScriptUrl && controllerScriptUrl === activeScriptUrl,
        ),
        scope: registration?.scope ?? null,
        activeScriptUrl,
      };
    }),
  };
}

async function unregisterAllControlledServiceWorkers(): Promise<void> {
  if (!('serviceWorker' in navigator)) {
    return;
  }

  const registrations = await navigator.serviceWorker.getRegistrations();
  for (const registration of registrations) {
    await registration.unregister();
  }
}

async function waitForControlledServiceWorkers(
  ids: ControlledServiceWorkerId[],
  timeoutMs = DEFAULT_SERVICE_WORKER_WAIT_TIMEOUT_MS,
): Promise<ServiceWorkerListResult> {
  const unavailableIds = ids.filter(id => !isServiceWorkerAvailable(id));
  if (unavailableIds.length > 0) {
    throw new Error(
      `Requested unavailable service workers: ${unavailableIds.join(', ')}`,
    );
  }

  const deadline = Date.now() + timeoutMs;
  while (Date.now() <= deadline) {
    const snapshot = await listControlledServiceWorkers();
    const readyIds = snapshot.workers
      .filter(worker => worker.registered)
      .map(worker => worker.id);

    if (ids.every(id => readyIds.includes(id))) {
      return snapshot;
    }

    await new Promise(resolve =>
      setTimeout(resolve, SERVICE_WORKER_WAIT_INTERVAL_MS),
    );
  }

  throw new Error(`Timed out waiting for service workers: ${ids.join(', ')}`);
}

function exposeE2EServiceWorkerApi() {
  const globalWindow = window as Window & {
    [BROWSER_TEST_SERVICE_WORKER_NAMESPACE]?: {
      serviceWorkers: E2EServiceWorkersApi;
    };
  };

  globalWindow[BROWSER_TEST_SERVICE_WORKER_NAMESPACE] = {
    serviceWorkers: {
      list: listControlledServiceWorkers,
      unregisterAll: unregisterAllControlledServiceWorkers,
      waitFor: waitForControlledServiceWorkers,
    },
  };
}

/**
 * Warms up the sync-message worker so the registration is already done by the
 * time a student opens an IDE or a lesson.
 *
 * Failures here are recorded but never prompt the user. This runs from
 * `bootstrapCipApp` before the first render — so before routing — which means
 * it also runs on pages with no code runner at all (the landing page,
 * `/cipx/intro`), where a modal about service workers means nothing to the
 * reader.
 *
 * Failing is safe because this is only a warm-up. The worker calls
 * `skipWaiting()` + `clients.claim()`, so a later registration still takes
 * control of this tab without a reload, and both
 * `CodeProvider.requestPythonClient` and `writeMessageServiceWorker`'s runtime
 * recovery re-register on demand. A page that can actually run code still
 * prompts: if `CodeProvider`'s own attempt fails it calls
 * `handleServiceWorkerError`, so the only thing suppressed here is a modal on
 * a page that was never going to run Python.
 */
async function startSyncMessageServiceWorker() {
  if (!('serviceWorker' in navigator)) {
    await reportServiceWorkerFailure({
      serviceWorkerSupported: false,
    });
    return;
  }

  try {
    await ensureSyncMessageServiceWorkerReady();
  } catch (error) {
    if (import.meta.env.VITE_BROWSER_TEST_MODE === 'true') {
      console.error(
        '[ServiceWorkerController] Failed to start sync-message worker',
        error,
      );
      return;
    }

    await reportServiceWorkerFailure({
      error,
      serviceWorkerSupported: true,
    });
  }
}

function startPwaServiceWorker() {
  if (!isServiceWorkerAvailable('pwa')) {
    console.warn(
      '[ServiceWorkerController] PWA service worker requested but unavailable in this environment.',
    );
    return;
  }

  registerPwaServiceWorker();
}

/**
 * Starts the requested service workers and exposes the browser-test diagnostics bridge
 * on `window.__cipBrowserTest`.
 */
export async function startConfiguredServiceWorkers(): Promise<void> {
  if (typeof window === 'undefined') {
    return;
  }

  exposeE2EServiceWorkerApi();

  const requestedIds = getRequestedServiceWorkerIds();
  if (requestedIds.length === 0) {
    return;
  }

  if (requestedIds.includes('sync-message')) {
    await startSyncMessageServiceWorker();
  }

  if (requestedIds.includes('pwa')) {
    startPwaServiceWorker();
  }
}
